import React, { useCallback, useMemo } from 'react'
import { Card, Collapse, Input, Space, Switch, Tag, Tooltip, Typography } from 'antd'
import { v4 as uuidv4 } from 'uuid'
import { DatahubColumnsEditor } from './DatahubColumnsEditor'
import { DatahubFilterBuilder } from './DatahubFilterBuilder'
import { DatahubGeneralPanel } from './DatahubGeneralPanel'
import { CnDatahubCategoryAccess, CnDatahubCategoryColumn, SharedCategoryConfig } from './datahub-interfaces'
import { createDefaultDatahubCategoryAccess, ensureSharedCategoryFilter } from './datahubDefaults'
import { DatahubFilterGroup, isFilterJsonValid, parseFilterJson, serializeFilterGroup } from './datahubFilter'
import { InfoCircleOutlined } from '@ant-design/icons'
import { orderKeyForMove, sortByOrderKey } from './datahubOrderKey'

interface CnDatahubSharedCategoryProps {
  config: SharedCategoryConfig
  orgaId: string
  onChange: (config: SharedCategoryConfig) => void
}

/**
 * Check if a column is overridden
 * @param defaultColumnId - The id of the default column
 * @param overrides - The overrides
 * @returns true if the column is overridden, false otherwise
 */
function isColumnOverridden(
  defaultColumnId: string,
  overrides: CnDatahubCategoryColumn[],
): boolean {
  return overrides.some((col) => col.categoryColumnId === defaultColumnId)
}

function getOverrideForColumn(
  defaultColumnId: string,
  overrides: CnDatahubCategoryColumn[],
): CnDatahubCategoryColumn | undefined {
  return overrides.find((col) => col.categoryColumnId === defaultColumnId)
}

/**
 * Merge a default column with an override
 * @param defaultColumn - The default column
 * @param override - The override
 * @returns The merged column
 */
function mergeColumnWithOverride(
  defaultColumn: CnDatahubCategoryColumn,
  override?: CnDatahubCategoryColumn,
): CnDatahubCategoryColumn {
  if (!override) return defaultColumn
  return { ...defaultColumn, ...override, id: override.id, categoryColumnId: defaultColumn.id }
}

export function CnDatahubSharedCategory({
  config,
  orgaId,
  onChange,
}: CnDatahubSharedCategoryProps) {
  const enabled = config.access !== null

  /**
   * Toggle the enabled state of the category
   * Update the config in the parent component
   * @param checked - The new enabled state
   */
  const toggleEnabled = useCallback(
    (checked: boolean) => {
      if (checked) {
        const access = createDefaultDatahubCategoryAccess(orgaId, config.category.id)
        if (!access) return
        onChange({
          ...config,
          access,
          filter: ensureSharedCategoryFilter({ access, filter: null }),
          columnOverrides: [],
        })
      } else {
        onChange({
          ...config,
          access: null,
          filter: null,
          columnOverrides: [],
        })
      }
    },
    [config, onChange, orgaId],
  )

  /**
   * Update the access of the category
   * @param partialAccess - The partial access to apply to the access (partial of CnDatahubCategoryAccess)
   */
  const updateAccess = useCallback(
    (partialAccess: Partial<CnDatahubCategoryAccess>) => {
      if (!config.access) return
      onChange({
        ...config,
        access: { ...config.access, ...partialAccess },
      })
    },
    [config, onChange],
  )

  /**
   * Update the filter of the category from the visual builder
   */
  const updateFilterGroup = useCallback(
    (group: DatahubFilterGroup) => {
      if (!config.access) return
      const filter = ensureSharedCategoryFilter(config)
      if (!filter) return
      onChange({
        ...config,
        filter: { ...filter, jsonFilter: serializeFilterGroup(group) },
      })
    },
    [config, onChange],
  )

  const activeFilter = useMemo(() => ensureSharedCategoryFilter(config), [config])

  const filterGroup = useMemo(
    () => parseFilterJson(activeFilter?.jsonFilter),
    [activeFilter?.jsonFilter],
  )

  /**
   * Toggle the override of a column (click on 'Personnaliser' button)
   * @param defaultColumn - The default column
   * @param checked - The new state of the column override
   */
  const toggleColumnOverride = useCallback(
    (defaultColumn: CnDatahubCategoryColumn, checked: boolean) => {
      if (!config.access) return
      if (checked) {
        // Create a new override
        const override: CnDatahubCategoryColumn = {
          ...defaultColumn,
          id: uuidv4(),
          categoryAccessId: config.access.id,
          categoryColumnId: defaultColumn.id,
        }
        onChange({
          ...config,
          columnOverrides: [...config.columnOverrides, override],
        })
      } else {
        // Remove the override
        onChange({
          ...config,
          columnOverrides: config.columnOverrides.filter(
            (col) => col.categoryColumnId !== defaultColumn.id,
          ),
        })
      }
    },
    [config, onChange],
  )

  /**
   * Update a column override
   * @param columnId - The id of the column to update
   * @param patch - The patch to apply to the column
   */
  const updateColumnOverride = useCallback(
    (columnId: string, patch: Partial<CnDatahubCategoryColumn>) => {
      onChange({
        ...config,
        columnOverrides: config.columnOverrides.map((col) =>
          col.id === columnId ? { ...col, ...patch } : col,
        ),
      })
    },
    [config, onChange],
  )

  /**
   * Upsert the order key of a column override
   * @param defaultColumn - The default column
   * @param newOrderKey - The new order key
   */
  const upsertColumnOrderOverride = useCallback(
    (defaultColumn: CnDatahubCategoryColumn, newOrderKey: number) => {
      if (!config.access) return

      const existingOverride = getOverrideForColumn(defaultColumn.id, config.columnOverrides)
      if (existingOverride) {
        updateColumnOverride(existingOverride.id, { orderKey: newOrderKey })
        return
      }

      const override: CnDatahubCategoryColumn = {
        ...defaultColumn,
        id: uuidv4(),
        categoryAccessId: config.access.id,
        categoryColumnId: defaultColumn.id,
        orderKey: newOrderKey,
      }
      onChange({
        ...config,
        columnOverrides: [...config.columnOverrides, override],
      })
    },
    [config, onChange, updateColumnOverride],
  )

  /**
   * Move a column to a new position
   * @param fromIndex - The index of the column to move
   * @param toIndex - The new index of the column
   */
  const moveColumnTo = useCallback(
    (fromIndex: number, toIndex: number) => {
      if (fromIndex === toIndex) return

      const displayColumns = config.defaultColumns.map((column) =>
        mergeColumnWithOverride(column, getOverrideForColumn(column.id, config.columnOverrides)),
      )
      const sorted = sortByOrderKey(displayColumns)
      // Get the column to move
      const moved = sorted[fromIndex]
      // Check if the column is valid
      if (!moved || toIndex < 0 || toIndex >= sorted.length) return

      const defaultColumnId = moved.categoryColumnId ?? moved.id
      // Get the default column
      const defaultColumn = config.defaultColumns.find((column) => column.id === defaultColumnId)
      if (!defaultColumn) return

      // Get the new order key
      const newOrderKey = orderKeyForMove(sorted, fromIndex, toIndex)
      // Update the order key of the default column
      upsertColumnOrderOverride(defaultColumn, newOrderKey)
    },
    [config.columnOverrides, config.defaultColumns, upsertColumnOrderOverride],
  )
  /**
   * Check if the filter is valid
   * @returns true if the filter is valid, false otherwise
   */
  const filterIsValid = useMemo(() => {
    if (!enabled || !activeFilter) return true
    return isFilterJsonValid(activeFilter.jsonFilter, config.defaultColumns)
  }, [activeFilter, config.defaultColumns, enabled])

  /**
   * Sort the display columns by order key
   */
  const sortedDisplayColumns = useMemo(() => {
    // Merge the default columns with the overridden columns
    const items = config.defaultColumns.map((defaultColumn) => ({
      defaultColumn,
      displayColumn: mergeColumnWithOverride(
        defaultColumn,
        getOverrideForColumn(defaultColumn.id, config.columnOverrides),
      ),
    }))
    // Sort the columns by order key
    return sortByOrderKey(
      items.map((item) => ({
        item,
        orderKey: item.displayColumn.orderKey,
        datakey: item.displayColumn.datakey,
      })),
    ).map(({ item }) => item)
  }, [config.columnOverrides, config.defaultColumns])

  /**
   * Get the keys of the overridden columns
   */
  const overriddenColumnKeys = useMemo(
    () =>
      sortedDisplayColumns
        .filter(({ defaultColumn }) => isColumnOverridden(defaultColumn.id, config.columnOverrides))
        .map(({ defaultColumn }) => defaultColumn.id),
    [config.columnOverrides, sortedDisplayColumns],
  )

  /**
   * Get the display columns (= default columns merged with overridden columns)
   */
  const displayColumns = useMemo(
    () => sortedDisplayColumns.map(({ displayColumn }) => displayColumn),
    [sortedDisplayColumns],
  )

  /**
   * Handle the change of the display columns
   * @param updatedColumns - The updated columns
   */
  const handleDisplayColumnsChange = useCallback(
    (updatedColumns: CnDatahubCategoryColumn[]) => {
      // Update the column overrides
      onChange({
        ...config,
        columnOverrides: config.columnOverrides.map((override) => {
          // Find the updated column in the updated columns
          const updated = updatedColumns.find(
            (col) =>
              col.id === override.id || col.categoryColumnId === override.categoryColumnId,
          )
          if (!updated) return override
          // Update the override with the updated column (only the fields that can be overridden)
          return {
            ...override,
            name: updated.name,
            required: updated.required,
            visible: updated.visible,
            description: updated.description,
          }
        }),
      })
    },
    [config, onChange],
  )

  /**
   * Handle the toggle of a column override (click on 'Personnaliser' button)
   * @param column - The column to toggle
   * @param checked - The new state of the column override
   */
  const handleToggleColumnOverride = useCallback(
    (column: CnDatahubCategoryColumn, checked: boolean) => {
      const defaultId = column.categoryColumnId ?? column.id
      const defaultColumn = config.defaultColumns.find((col) => col.id === defaultId)
      if (defaultColumn) toggleColumnOverride(defaultColumn, checked)
    },
    [config.defaultColumns, toggleColumnOverride],
  )

  /**
   * Check if a column is overridden
   * @param column - The column to check
   * @returns true if the column is overridden, false otherwise
   */
  const isDisplayColumnOverridden = useCallback(
    (column: CnDatahubCategoryColumn) =>
      isColumnOverridden(column.categoryColumnId ?? column.id, config.columnOverrides),
    [config.columnOverrides],
  )

  return (
    <Card
      size="small"
      title={config.category.name}
      extra={
        <Space size="small">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Activer
          </Typography.Text>
          <Switch size="small" checked={enabled} onChange={toggleEnabled} />
        </Space>
      }
      style={{ marginBottom: 8 }}
      styles={{ body: { padding: enabled ? 12 : 0 } }}
    >
      {enabled && (
        <Collapse
          size="small"
          defaultActiveKey={['general', 'filter', 'columns']}
          items={[
            {
              key: 'general',
              label: 'Général',
              children: config.access && (
                <DatahubGeneralPanel
                  access={config.access}
                  onChange={updateAccess}
                  categoryNameInput={
                    <>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Nom affiché pour l'orga si différent du nom par défaut
                      </Typography.Text>
                      <Input
                        size="small"
                        value={config.access?.overrideName ?? ''}
                        placeholder={config.category.name}
                        onChange={(e) => updateAccess({ overrideName: e.target.value || null })}
                        style={{ marginTop: 2 }}
                      />
                    </>
                  }
                  mode="shared"
                />
              ),
            },
            {
              key: 'filter',
              label: (
                <Space size="small">
                  <span>Filtre</span>
                  <Tag color="red" style={{ margin: 0 }}>
                    Obligatoire
                  </Tag>
                  {!filterIsValid && (
                    <Tag color="error" style={{ margin: 0 }}>
                      Invalide
                    </Tag>
                  )}
                </Space>
              ),
              children: (
                <>
                  <DatahubFilterBuilder
                    value={filterGroup}
                    columns={config.defaultColumns}
                    onChange={updateFilterGroup}
                  />
                  {!filterIsValid && (
                    <Typography.Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                      Le filtre est obligatoire : ajoutez au moins une condition complète et
                      corrigez les valeurs signalées en rouge.
                    </Typography.Text>
                  )}
                </>
              ),
            },
            {
              key: 'columns',
              label: (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text strong>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      Colonnes
                      <Tooltip title="Pour effectuer toute action sur la configuration d'une colonne par défaut, cliquez sur le bouton 'Personnaliser'">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </div>
                  </Typography.Text>
                  <Tag color="blue">{sortedDisplayColumns.length} colonne{sortedDisplayColumns.length > 1 ? 's' : ''}
                    {overriddenColumnKeys.length > 0 &&
                      ` · ${overriddenColumnKeys.length} personnalisée${overriddenColumnKeys.length > 1 ? 's' : ''}`}</Tag>
                </div>
              ),
              children: (
                <DatahubColumnsEditor
                  columns={displayColumns}
                  categoryId={config.category.id}
                  categoryAccessId={config.access?.id}
                  mode="shared"
                  onChange={handleDisplayColumnsChange}
                  isColumnOverridden={isDisplayColumnOverridden}
                  onToggleOverride={handleToggleColumnOverride}
                  onReorder={moveColumnTo}
                />
              ),
            },
          ]}
        />
      )}
    </Card>
  )
}
